import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { Logo } from './Logo';
import { Footer } from './Footer';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { api } from '../lib/api';
import type { AppUser } from '../types';
import { cn } from '../lib/utils';
import { LAYOUT } from '../styles/layout-tokens';

export function PublicShell({
  children,
  headerVariant = 'solid',
  bgClassName,
}: {
  children: ReactNode;
  // 'transparent' floats the nav over the hero glow (no bg/border) — used by the
  // redesigned home page; other marketing pages keep the solid sticky bar.
  headerVariant?: 'solid' | 'transparent';
  bgClassName?: string;
}) {
  const { t } = useTranslation();
  const [user, setUser] = useState<AppUser | null>(null);
  const transparent = headerVariant === 'transparent';

  useEffect(() => {
    api.getMe().then(setUser).catch(() => {});
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
      window.location.href = '/';
    } catch {
      // Lazy-load sonner only when a toast is actually needed (rare logout
      // error). Keeps the ~10 KB toast lib out of the eager landing entry
      // chunk — PublicShell wraps every prerendered marketing page.
      const { toast } = await import('sonner');
      toast.error(t('toasts.logoutFailed'));
    }
  };

  return (
    <div className={cn('min-h-screen flex flex-col', bgClassName ?? 'bg-zinc-50 dark:bg-zinc-950')}>
      <header
        className={cn(
          'z-40',
          transparent
            ? 'relative bg-transparent'
            : 'sticky top-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900',
        )}
      >
        <div className={cn(LAYOUT.container.wide, 'mx-auto', LAYOUT.page.paddingX, transparent ? 'py-4 sm:py-5' : LAYOUT.header.height, 'flex items-center justify-between')}>
          <Link href={user ? '/home' : '/'} className="flex items-center gap-3">
            <Logo className="h-7 w-auto text-zinc-900 dark:text-zinc-50" />
            <span className={cn('font-bold text-lg tracking-tight brand-text', transparent && 'font-display')}>
              {t('app.name')}
            </span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            {user && <UserMenu user={user} onLogout={handleLogout} />}
          </div>
        </div>
      </header>

      {children}

      <Footer />
    </div>
  );
}
